import { useState } from "react";
import { BookOpen, Gamepad2, Users, CircleDollarSign, Server, Wrench, HelpCircle, MapPin, Spade, Cpu, Vault, Landmark, ShieldHalf, Search, CornerDownRight } from "lucide-react";
import { WIKI_SECTIONS, renderWikiBody, searchWiki } from "./wikiContent";
import { useI18n } from "@/i18n";

const ICONS = { gamepad: Gamepad2, users: Users, dollar: CircleDollarSign, server: Server, wrench: Wrench, help: HelpCircle, map: MapPin, spade: Spade, cpu: Cpu, vault: Vault, landmark: Landmark, shield: ShieldHalf };

/* In-menu wiki — same knowledge base as the public site, readable in-game
   without leaving the menu. Search box up top for mid-robbery lookups. */
export function MenuWikiPanel({ onBack, accent = "#2E6BFF", audio }) {
  const { t } = useI18n();
  const [active, setActive] = useState("getting-started");
  const [query, setQuery] = useState("");
  const section = WIKI_SECTIONS.find((s) => s.id === active);
  const searching = query.trim().length >= 2;
  const results = searching ? searchWiki(query) : [];

  return (
    <div data-testid="menu-wiki" className="absolute inset-0 flex flex-col px-6 sm:px-10 pt-20 pb-10 overflow-hidden" style={{ animation: "ncFadeIn 0.3s ease both" }}>
      <div className="flex items-center gap-3 shrink-0">
        <button data-testid="wiki-back" onClick={onBack} className="font-mono text-[10px] tracking-[0.3em] uppercase text-zinc-400 hover:text-zinc-100 transition-colors mr-2">‹ {t("settings.back")}</button>
        <h2 className="font-semibold uppercase tracking-[0.2em] text-lg sm:text-xl text-zinc-50 flex items-center gap-2.5">
          <BookOpen className="w-5 h-5" style={{ color: accent }} /> {t("nav.wiki")}
        </h2>
        <span className="font-mono text-[9px] text-zinc-500 tracking-[0.2em] uppercase hidden sm:inline">the official knowledge base, in your menu</span>
        <div className="ml-auto relative w-48 sm:w-64">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
          <input
            data-testid="menu-wiki-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search the wiki…"
            className="w-full bg-black/50 border border-white/10 focus:border-white/30 pl-9 pr-3 py-2 font-mono text-[10px] tracking-[0.1em] text-zinc-100 placeholder:text-zinc-600 focus:outline-none transition-colors"
          />
        </div>
      </div>

      <div className="flex-1 min-h-0 mt-6 grid grid-cols-1 md:grid-cols-[240px_1fr] gap-4">
        <div className="flex md:flex-col gap-1 overflow-x-auto md:overflow-y-auto shrink-0 md:pr-1" style={{ scrollbarWidth: "thin" }}>
          {searching ? (
            results.length === 0 ? (
              <p data-testid="menu-wiki-search-empty" className="font-mono text-[10px] text-zinc-600 uppercase tracking-[0.15em] px-4 py-3">No hits for "{query.trim()}"</p>
            ) : (
              results.map((r) => {
                const Icon = ICONS[r.icon] || Gamepad2;
                return (
                  <button key={r.id} data-testid={`menu-wiki-search-result-${r.id}`}
                    onClick={() => { setActive(r.id); setQuery(""); audio?.tick?.(); }}
                    className="text-left px-4 py-3 border-l-2 border-white/10 hover:border-white/40 text-zinc-400 hover:text-zinc-100 transition-all whitespace-normal md:whitespace-nowrap">
                    <span className="font-mono text-[10px] tracking-[0.15em] uppercase flex items-center gap-2">
                      <Icon className="w-3.5 h-3.5 shrink-0" style={{ color: accent }} /> {r.label}
                      <CornerDownRight className="w-3 h-3 text-zinc-600" />
                    </span>
                    <span className="font-mono text-[9px] text-zinc-500 leading-relaxed block mt-1 normal-case tracking-normal line-clamp-2">{r.snippet}</span>
                  </button>
                );
              })
            )
          ) : (
            WIKI_SECTIONS.map((s) => {
              const Icon = ICONS[s.icon] || Gamepad2;
              return (
                <button key={s.id} data-testid={`menu-wiki-sec-${s.id}`} onClick={() => { setActive(s.id); audio?.tick?.(); }}
                  className={`text-left font-mono text-[10px] tracking-[0.15em] uppercase px-4 py-3 border-l-2 whitespace-nowrap flex items-center gap-2.5 transition-all ${active === s.id ? "text-zinc-50 bg-white/[0.05]" : "border-white/10 text-zinc-500 hover:text-zinc-200 hover:border-white/30"}`}
                  style={active === s.id ? { borderColor: accent } : {}}>
                  <Icon className="w-3.5 h-3.5 shrink-0" /> {s.label}
                </button>
              );
            })
          )}
        </div>
        <div className="min-w-0 border border-white/10 bg-black/50 p-6 sm:p-8 overflow-y-auto" style={{ scrollbarWidth: "thin" }}>
          <p className="font-sans font-bold uppercase text-xl text-zinc-50 mb-6 pb-4 border-b border-white/10">{section.label}</p>
          {renderWikiBody(section.body)}
        </div>
      </div>
    </div>
  );
}
